import React, { useEffect, useRef, useState } from "react";
import {
  fetchQuranChapters,
  fetchQuranicTopics,
  fetchBuiltinQuranicTopics,
  createQuranicTopic,
  deleteQuranicTopic,
} from "../api";
import Surface from "../theme/Surface";

const TIER_LABELS = {
  core: "Core",
  stretch_70: "Stretch",
  advanced: "Advanced",
};

// Tier badge colours mapped to the dark-theme tokens.
const TIER_STYLE = {
  core: { color: "var(--tag-teal)", background: "var(--tag-teal-bg)", borderColor: "transparent" },
  stretch_70: { color: "var(--gold-bright)", background: "var(--gold-bg)", borderColor: "rgba(201,162,39,0.3)" },
  advanced: { color: "var(--tag-violet)", background: "var(--tag-violet-bg)", borderColor: "transparent" },
};

/**
 * QuranicTopics — the top-level Quranic learning section.
 * Shows two tabs: Built-in curriculum topics and user-created topics.
 * - Built-in topics: imported from JSON, visible to all, non-editable
 * - User-created topics: created by student, editable/deletable
 */
export default function QuranicTopics({ onTopicSelect, onProgressUpdate, builtinRefreshKey, selectedTopicId, selectedTopicIsBuiltin }) {
  const [activeTab, setActiveTab] = useState("builtin"); // "builtin" | "my"
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsError, setTopicsError] = useState(null);

  const [builtinTopics, setBuiltinTopics] = useState([]);
  const [builtinLoading, setBuiltinLoading] = useState(true);
  const [builtinError, setBuiltinError] = useState(null);
  const [curriculumInfoExpanded, setCurriculumInfoExpanded] = useState(false);
  const [myTopicsInfoExpanded, setMyTopicsInfoExpanded] = useState(false);

  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedSurah, setSelectedSurah] = useState("");
  const [ayahStart, setAyahStart] = useState("");
  const [ayahEnd, setAyahEnd] = useState("");
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  // Load both lists on mount
  useEffect(() => {
    loadTopics();
    loadBuiltinTopics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch builtin topics when a builtin quiz is completed
  useEffect(() => {
    if (builtinRefreshKey > 0) loadBuiltinTopics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builtinRefreshKey]);

  // Auto-open the first Quranic topic when the section loads so the detail pane
  // isn't empty. Prefer the curriculum (default tab); fall back to user topics.
  // Runs once per mount and only when nothing is already selected — so it won't
  // override a topic the user picks (or one restored from navigation).
  //
  // Desktop only (md+): there the detail pane is always visible, so opening the
  // first topic fills it. On mobile it would force a jump straight into the
  // detail view, so we leave the user on the topic list instead.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (selectedTopicId) { autoSelectedRef.current = true; return; }
    if (builtinLoading || topicsLoading) return;
    const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop) return;
    if (builtinTopics.length > 0) {
      autoSelectedRef.current = true;
      onTopicSelect(builtinTopics[0].id, builtinTopics[0].title, true);
    } else if (topics.length > 0) {
      autoSelectedRef.current = true;
      onTopicSelect(topics[0].id, topics[0].surah_name, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builtinLoading, topicsLoading, builtinTopics, topics, selectedTopicId]);

  // Load chapters when create form opens
  useEffect(() => {
    if (showCreate && chapters.length === 0) {
      loadChapters();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate]);

  async function loadTopics() {
    try {
      setTopicsLoading(true);
      setTopicsError(null);
      const data = await fetchQuranicTopics();
      const list = Array.isArray(data) ? data : [];
      setTopics(list);
      if (onProgressUpdate) {
        onProgressUpdate({ completed: list.filter((t) => t.completed).length, total: list.length });
      }
    } catch (err) {
      setTopicsError(err.message || "Could not load Quranic topics");
    } finally {
      setTopicsLoading(false);
    }
  }

  async function loadBuiltinTopics() {
    try {
      setBuiltinLoading(true);
      setBuiltinError(null);
      const data = await fetchBuiltinQuranicTopics();
      const list = Array.isArray(data) ? data : [];
      setBuiltinTopics(list);
    } catch (err) {
      setBuiltinError(err.message || "Could not load curriculum topics");
    } finally {
      setBuiltinLoading(false);
    }
  }

  async function loadChapters() {
    try {
      setChaptersLoading(true);
      const data = await fetchQuranChapters();
      setChapters(data.chapters || []);
    } catch {
      // Silently fail — user can still type surah number
    } finally {
      setChaptersLoading(false);
    }
  }

  const selectedChapter = chapters.find((ch) => String(ch.id) === String(selectedSurah));
  const maxAyah = selectedChapter?.verses_count || 999;

  async function handleCreate(e) {
    e.preventDefault();
    setCreateError(null);
    const surahId = parseInt(selectedSurah, 10);
    const start = parseInt(ayahStart, 10);
    const end = parseInt(ayahEnd, 10);
    if (!surahId || !start || !end) {
      setCreateError("Please select a Surah and enter a valid ayah range.");
      return;
    }
    if (start > end) {
      setCreateError("Ayah start must be ≤ ayah end.");
      return;
    }
    if (end > maxAyah) {
      setCreateError(`This Surah only has ${maxAyah} ayahs.`);
      return;
    }
    if (end - start >= 20) {
      setCreateError("Please select a range of 20 ayahs or fewer.");
      return;
    }
    const surahName = selectedChapter
      ? `${selectedChapter.name_arabic} (${selectedChapter.name_simple})`
      : `Surah ${surahId}`;
    try {
      setCreating(true);
      const newTopic = await createQuranicTopic({
        surah_id: surahId,
        surah_name: surahName,
        ayah_start: start,
        ayah_end: end,
      });
      setTopics((prev) => [newTopic, ...prev]);
      setShowCreate(false);
      setSelectedSurah("");
      setAyahStart("");
      setAyahEnd("");
    } catch (err) {
      let msg = err.message || "Could not create topic";
      // Parse Django REST framework validation errors into plain text
      try {
        const parsed = JSON.parse(msg);
        const all = [
          ...(parsed.non_field_errors || []),
          ...(parsed.ayah_end || []),
          ...(parsed.ayah_start || []),
          ...(parsed.detail ? [parsed.detail] : []),
        ];
        if (all.length) msg = all.join(" ");
      } catch (_) { /* not JSON, use as-is */ }
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(topicId) {
    if (!window.confirm("Delete this Quranic topic?")) return;
    try {
      setDeletingId(topicId);
      await deleteQuranicTopic(topicId);
      setTopics((prev) => prev.filter((t) => t.id !== topicId));
    } catch (err) {
      alert(err.message || "Could not delete topic");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Section header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <span className="text-2xl">🕌</span>
            Quranic Topics
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Learn Arabic directly from the Quran</p>
        </div>
        {activeTab === "my" && (
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 transition-[filter]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex rounded-xl bg-slate-100 p-1 gap-1">
        <button
          onClick={() => setActiveTab("builtin")}
          className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            activeTab === "builtin" ? "bg-white text-dt-gold-bright shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          📚 Curriculum
        </button>
        <button
          onClick={() => setActiveTab("my")}
          className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            activeTab === "my" ? "bg-white text-dt-gold-bright shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          📖 My Topics
        </button>
      </div>

      {/* Progress bar — curriculum tab */}
      {activeTab === "builtin" && builtinTopics.length > 0 && (() => {
        const completed = builtinTopics.filter((t) => t.completed).length;
        const total = builtinTopics.length;
        return (
          <div className="-mt-1">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Curriculum completed</span>
              <span className="tabular-nums font-medium text-slate-700">{completed}/{total}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-teal-500 transition-all duration-500"
                style={{ width: total > 0 ? `${Math.round((completed / total) * 100)}%` : "0%" }}
              />
            </div>
          </div>
        );
      })()}

      {/* Progress bar — my topics tab */}
      {activeTab === "my" && topics.length > 0 && (() => {
        const completed = topics.filter((t) => t.completed).length;
        const total = topics.length;
        return (
          <div className="-mt-1">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>My topics completed</span>
              <span className="tabular-nums font-medium text-slate-700">{completed}/{total}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-amber-500 transition-all duration-500"
                style={{ width: total > 0 ? `${Math.round((completed / total) * 100)}%` : "0%" }}
              />
            </div>
          </div>
        );
      })()}

      {/* Built-in curriculum tab */}
      {activeTab === "builtin" && (
        <>
          {builtinLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-3 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : builtinError ? (
            <p className="text-sm text-red-600 px-1">{builtinError}</p>
          ) : builtinTopics.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <div className="text-4xl mb-2">📚</div>
              <p className="text-sm">No curriculum topics yet.</p>
              <p className="text-xs mt-1">An admin can import the curriculum via the Admin Panel.</p>
            </div>
          ) : (
            <>
              {/* Curriculum info card */}
              <div className="mb-3 rounded-xl surface-glass overflow-hidden">
                <button
                  onClick={() => setCurriculumInfoExpanded((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-dt-parchment">
                    <span>📖</span>
                    <span>About this Curriculum</span>
                  </span>
                  <svg
                    className={`w-4 h-4 text-dt-gold-dim transition-transform ${curriculumInfoExpanded ? "rotate-180" : ""}`}
                    fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <p className="px-3 pb-2 text-xs text-dt-parchment-dim">
                  Selected passages covering the most frequent Quranic vocabulary.
                </p>
                {curriculumInfoExpanded && (
                  <div className="px-3 pb-3 text-xs text-dt-parchment-dim space-y-2 border-t border-teal-200 pt-2">
                    <p>
                      This is <strong>not the full Quran</strong> — it is a curated selection of Surahs and Ayahs chosen
                      because they contain the highest-frequency Arabic vocabulary in the Quran.
                    </p>
                    <p>
                      Studying all topics gives you recognition of approximately <strong>85% of word tokens</strong> you
                      will encounter when reading the Quran. Each topic includes interactive word definitions,
                      part-of-speech highlighting, and a quiz to reinforce learning.
                    </p>
                  </div>
                )}
              </div>

              <ul className="space-y-2">
              {builtinTopics.map((topic) => {
                const tierLabel = TIER_LABELS[topic.curriculum_tier] || topic.curriculum_tier;
                const tierStyle = TIER_STYLE[topic.curriculum_tier] || { color: "var(--slate)", background: "transparent", borderColor: "var(--panel-line)" };
                const isSelected = selectedTopicIsBuiltin && selectedTopicId === topic.id;
                const scorePercent = topic.best_total > 0 ? Math.round(topic.best_score / topic.best_total * 100) : 0;
                return (
                  <li key={topic.id}>
                    <Surface
                      as="div"
                      glass
                      interactive
                      nudge
                      active={isSelected}
                      onClick={() => onTopicSelect(topic.id, topic.title, true)}
                      className="flex items-center gap-3 p-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-cormorant text-base font-semibold truncate" style={{ color: isSelected ? "var(--gold-bright)" : "var(--parchment)" }}>{topic.title}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full border font-semibold shrink-0" style={tierStyle}>
                            {tierLabel}
                          </span>
                          {topic.completed && (
                            <span className="text-xs shrink-0" style={{ color: "var(--tag-teal)" }}>✓</span>
                          )}
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: "var(--parchment-dim)" }}>
                          {topic.surah_name} · Ayahs {topic.ayah_start}–{topic.ayah_end}
                          {topic.estimated_minutes && ` · ~${topic.estimated_minutes} min`}
                        </p>
                        {topic.best_total > 0 && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="progress-track flex-1">
                              <div className="progress-fill" style={{ width: `${scorePercent}%` }} />
                            </div>
                            <span className="text-xs tabular-nums shrink-0" style={{ color: "var(--parchment-dim)" }}>
                              {topic.best_score}/{topic.best_total}
                            </span>
                          </div>
                        )}
                      </div>
                      <svg className="w-4 h-4 shrink-0" style={{ color: isSelected ? "var(--gold-bright)" : "var(--parchment-dim)" }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Surface>
                  </li>
                );
              })}
            </ul>
            </>
          )}
        </>
      )}

      {/* My Topics tab */}
      {activeTab === "my" && (
        <>
          {/* My Topics info card */}
          <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
            <button
              onClick={() => setMyTopicsInfoExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-left"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-amber-800">
                <span>📝</span>
                <span>About My Topics</span>
              </span>
              <svg
                className={`w-4 h-4 text-amber-600 transition-transform ${myTopicsInfoExpanded ? "rotate-180" : ""}`}
                fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <p className="px-3 pb-2 text-xs text-amber-700">
              Study any passage from the Quran at your own pace.
            </p>
            {myTopicsInfoExpanded && (
              <div className="px-3 pb-3 text-xs text-amber-700 space-y-2 border-t border-amber-200 pt-2">
                <p>
                  Create your own study topics by picking any Surah and Ayah range. Great for focusing on
                  a passage your teacher assigned, a Surah you are memorising, or exploring beyond the
                  curriculum.
                </p>
                <p>
                  <strong>Max 20 ayahs per topic.</strong> Verse text and audio are loaded live from
                  quran.com.
                </p>
              </div>
            )}
          </div>

          {/* Create form */}
          {showCreate && (
            <form
              onSubmit={handleCreate}
              className="bg-slate-50 rounded-2xl border border-slate-200 p-4 flex flex-col gap-3"
            >
              <h3 className="text-sm font-semibold text-slate-700">Create Quranic Topic</h3>

              {/* Surah selector */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Surah</label>
                {chaptersLoading ? (
                  <p className="text-xs text-slate-400">Loading surahs…</p>
                ) : (
                  <select
                    value={selectedSurah}
                    onChange={(e) => { setSelectedSurah(e.target.value); setAyahStart(""); setAyahEnd(""); }}
                    required
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">— Select Surah —</option>
                    {chapters.map((ch) => (
                      <option key={ch.id} value={ch.id}>
                        {ch.id}. {ch.name_arabic} — {ch.name_simple} ({ch.verses_count} ayahs)
                      </option>
                    ))}
                    {chapters.length === 0 && (
                      <option value="" disabled>Enter surah number below if list unavailable</option>
                    )}
                  </select>
                )}
                {chapters.length === 0 && !chaptersLoading && (
                  <input
                    type="number"
                    value={selectedSurah}
                    onChange={(e) => setSelectedSurah(e.target.value)}
                    placeholder="Surah number (1–114)"
                    min={1}
                    max={114}
                    className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                )}
              </div>

              {/* Ayah range */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ayah Start</label>
                  <input
                    type="number"
                    value={ayahStart}
                    onChange={(e) => setAyahStart(e.target.value)}
                    min={1}
                    max={maxAyah}
                    required
                    placeholder="e.g. 1"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ayah End</label>
                  <input
                    type="number"
                    value={ayahEnd}
                    onChange={(e) => setAyahEnd(e.target.value)}
                    min={ayahStart || 1}
                    max={maxAyah}
                    required
                    placeholder="e.g. 5"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {selectedChapter && (
                <p className="text-xs text-slate-400">
                  {selectedChapter.name_simple} has {selectedChapter.verses_count} ayahs.
                  {selectedChapter.translated_name && ` Meaning: ${selectedChapter.translated_name}`}
                </p>
              )}

              {createError && (
                <p className="text-sm text-red-600">{createError}</p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setCreateError(null); }}
                  className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 py-2 rounded-xl bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 transition-[filter] disabled:opacity-60"
                >
                  {creating ? "Creating…" : "Create Topic"}
                </button>
              </div>
            </form>
          )}

          {/* Topics list */}
          {topicsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-3 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : topicsError ? (
            <p className="text-sm text-red-600 px-1">{topicsError}</p>
          ) : topics.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <div className="text-4xl mb-2">📖</div>
              <p className="text-sm">No Quranic topics yet.</p>
              <p className="text-xs mt-1">Tap "New" to start learning from the Quran.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {topics.map((topic) => {
                const isSelected = !selectedTopicIsBuiltin && selectedTopicId === topic.id;
                const scorePercent = topic.best_total > 0 ? Math.round(topic.best_score / topic.best_total * 100) : 0;
                return (
                  <li key={topic.id}>
                    <Surface
                      as="div"
                      glass
                      interactive
                      nudge
                      active={isSelected}
                      onClick={() => onTopicSelect(topic.id, topic.surah_name, false)}
                      className="group flex items-center gap-3 p-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-arabic truncate" style={{ color: isSelected ? "var(--gold-bright)" : "var(--parchment)" }}>{topic.surah_name}</span>
                          {topic.completed && (
                            <span className="text-xs shrink-0" style={{ color: "var(--tag-teal)" }}>✓</span>
                          )}
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: "var(--parchment-dim)" }}>
                          Ayahs {topic.ayah_start}–{topic.ayah_end}
                        </p>
                        {topic.best_total > 0 && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="progress-track flex-1">
                              <div className="progress-fill" style={{ width: `${scorePercent}%` }} />
                            </div>
                            <span className="text-xs tabular-nums shrink-0" style={{ color: "var(--parchment-dim)" }}>
                              {topic.best_score}/{topic.best_total}
                            </span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(topic.id); }}
                        disabled={deletingId === topic.id}
                        className="p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:brightness-125"
                        style={{ color: "var(--tag-rose)" }}
                        aria-label="Delete"
                      >
                        {deletingId === topic.id ? (
                          <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--tag-rose)", borderTopColor: "transparent" }} />
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                      <svg className="w-4 h-4 shrink-0" style={{ color: isSelected ? "var(--gold-bright)" : "var(--parchment-dim)" }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Surface>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
